import { jsx, Fragment } from "react/jsx-runtime";
import * as React from "react";
import * as prismicH from "@prismicio/helpers";
import * as prismicR from "@prismicio/richtext";
import { PrismicLink } from "./PrismicLink.js";
import { usePrismicContext } from "./usePrismicContext.js";
const createDefaultSerializer = (args) => prismicR.wrapMapSerializer({
  heading1: ({ children, key }) => /* @__PURE__ */ jsx("h1", { children }, key),
  heading2: ({ children, key }) => /* @__PURE__ */ jsx("h2", { children }, key),
  heading3: ({ children, key }) => /* @__PURE__ */ jsx("h3", { children }, key),
  heading4: ({ children, key }) => /* @__PURE__ */ jsx("h4", { children }, key),
  heading5: ({ children, key }) => /* @__PURE__ */ jsx("h5", { children }, key),
  heading6: ({ children, key }) => /* @__PURE__ */ jsx("h6", { children }, key),
  paragraph: ({ children, key }) => /* @__PURE__ */ jsx("p", { children }, key),
  preformatted: ({ node, key }) => /* @__PURE__ */ jsx("pre", { children: node.text }, key),
  strong: ({ children, key }) => /* @__PURE__ */ jsx("strong", { children }, key),
  em: ({ children, key }) => /* @__PURE__ */ jsx("em", { children }, key),
  listItem: ({ children, key }) => /* @__PURE__ */ jsx("li", { children }, key),
  oListItem: ({ children, key }) => /* @__PURE__ */ jsx("li", { children }, key),
  list: ({ children, key }) => /* @__PURE__ */ jsx("ul", { children }, key),
  oList: ({ children, key }) => /* @__PURE__ */ jsx("ol", { children }, key),
  image: ({ node, key }) => {
    const img = /* @__PURE__ */ jsx("img", { src: node.url, alt: node.alt ?? void 0, "data-copyright": node.copyright ? node.copyright : void 0 });
    return /* @__PURE__ */ jsx("p", { className: "block-img", children: node.linkTo ? /* @__PURE__ */ jsx(PrismicLink, { linkResolver: args.linkResolver, internalComponent: args.internalLinkComponent, externalComponent: args.externalLinkComponent, field: node.linkTo, children: img }) : img }, key);
  },
  embed: ({ node, key }) => /* @__PURE__ */ jsx("div", { "data-oembed": node.oembed.embed_url, "data-oembed-type": node.oembed.type, "data-oembed-provider": node.oembed.provider_name, dangerouslySetInnerHTML: { __html: node.oembed.html ?? "" } }, key),
  hyperlink: ({ node, children, key }) => /* @__PURE__ */ jsx(PrismicLink, { field: node.data, linkResolver: args.linkResolver, internalComponent: args.internalLinkComponent, externalComponent: args.externalLinkComponent, children }, key),
  label: ({ node, children, key }) => /* @__PURE__ */ jsx("span", { className: node.data.label, children }, key),
  span: ({ text, key }) => {
    const result = [];
    let i = 0;
    for (const line of text.split("\n")) {
      if (i > 0) {
        result.push(/* @__PURE__ */ jsx("br", {}, `${i}__break`));
      }
      result.push(/* @__PURE__ */ jsx(React.Fragment, { children: line }, `${i}__line`));
      i++;
    }
    return /* @__PURE__ */ jsx(React.Fragment, { children: result }, key);
  }
});
const PrismicRichText = (props) => {
  const context = usePrismicContext();
  return React.useMemo(() => {
    if (prismicH.isFilled.richText(props.field)) {
      const linkResolver = props.linkResolver || context.linkResolver;
      const serializer = prismicR.composeSerializers(typeof props.components === "object" ? prismicR.wrapMapSerializer(props.components) : props.components, typeof context.richTextComponents === "object" ? prismicR.wrapMapSerializer(context.richTextComponents) : context.richTextComponents, createDefaultSerializer({
        linkResolver,
        internalLinkComponent: props.internalLinkComponent,
        externalLinkComponent: props.externalLinkComponent
      }));
      const serialized = prismicR.serialize(props.field, (type, node, text, children, key) => {
        const result = serializer(type, node, text, children, key);
        if (React.isValidElement(result) && result.key == null) {
          return React.cloneElement(result, { key });
        } else {
          return result;
        }
      });
      return /* @__PURE__ */ jsx(Fragment, { children: serialized });
    } else {
      return props.fallback != null ? /* @__PURE__ */ jsx(Fragment, { children: props.fallback }) : null;
    }
  }, [
    props.field,
    props.internalLinkComponent,
    props.externalLinkComponent,
    props.components,
    props.linkResolver,
    props.fallback,
    context.linkResolver,
    context.richTextComponents
  ]);
};
export {
  PrismicRichText
};
//# sourceMappingURL=PrismicRichText.js.map
